import { __getOfflineFile, __saveOfflineFile } from './storage';

const dataUrlToResponse = async (dataUrl) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new Response(blob, {
    status: 200,
    headers: {
      'Content-Type': blob.type || 'application/octet-stream'
    }
  });
};

const jsonResponse = (body, status = 200) => new Response(JSON.stringify(body), {
  status,
  headers: { 'Content-Type': 'application/json' }
});

const getPathname = (input) => {
  const raw = typeof input === 'string' ? input : input?.url;
  if (!raw) return '';
  try {
    return new URL(raw, window.location.origin).pathname;
  } catch {
    return raw;
  }
};

const formDataFromRequest = async (input, init = {}) => {
  if (init.body instanceof FormData) return init.body;
  if (input instanceof Request) return input.clone().formData();
  return new FormData();
};

export const installOfflineFetchHandlers = () => {
  if (typeof window === 'undefined' || window.__ybitOfflineFetchInstalled) return;
  window.__ybitOfflineFetchInstalled = true;

  const originalFetch = window.fetch.bind(window);
  const useDriveStorage = process.env.REACT_APP_STORAGE_MODE === 'drive';

  window.fetch = async (input, init = {}) => {
    const pathname = getPathname(input);
    const method = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();

    if (useDriveStorage && pathname.startsWith('/api/upload')) {
      return originalFetch(input, init);
    }

    if (pathname === '/api/health' || pathname === '/api/upload/test') {
      return jsonResponse({ success: true, status: 'ok', message: 'YBIT offline API is active' });
    }

    if (method === 'POST' && pathname === '/api/upload/achievement') {
      const formData = await formDataFromRequest(input, init);
      const file = formData.get('file');
      const rollNumber = formData.get('rollNumber') || 'unknown';
      if (!file || typeof file === 'string') {
        return jsonResponse({ success: false, error: 'No file uploaded' }, 400);
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `offline-files/achievements/${rollNumber}/${Date.now()}_${safeName}`;
      await __saveOfflineFile(filePath, file);

      return jsonResponse({
        success: true,
        filePath,
        publicUrl: `/${filePath}`,
        filename: safeName,
        size: file.size,
        rollNumber,
        message: 'File saved in this browser for offline use'
      });
    }

    if (method === 'POST' && (pathname === '/api/upload/photo' || pathname === '/api/upload-hod-photo')) {
      const formData = await formDataFromRequest(input, init);
      const file = formData.get('file') || formData.get('photo');
      const rollNumber = formData.get('rollNumber') || 'Hod';
      if (!file || typeof file === 'string') {
        return jsonResponse({ success: false, error: 'No file uploaded' }, 400);
      }

      const saved = await __saveOfflineFile(`photos/${rollNumber}/${Date.now()}_${file.name}`, file);
      return jsonResponse({
        success: true,
        photoUrl: saved.dataUrl,
        photoPath: `photos/${rollNumber}`,
        url: saved.dataUrl,
        message: 'Photo saved in this browser for offline use'
      });
    }

    if (method === 'DELETE' && pathname.startsWith('/api/upload/photo/')) {
      return jsonResponse({
        success: true,
        message: 'Photo reference removed from offline profile'
      });
    }

    if (pathname.startsWith('/offline-files/')) {
      const file = __getOfflineFile(pathname);
      if (file?.dataUrl) return dataUrlToResponse(file.dataUrl);
      return jsonResponse({ success: false, error: 'Offline file not found' }, 404);
    }

    return originalFetch(input, init);
  };
};
